import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  colorBackground,
  colorFieldsBorder,
  colorText,
  colorTextAdditional,
  colorAccent,
  colorHover,
  colorGray,
  colorLightBlue,
} from '../../../utils/palette';
import { borderRadius } from '../../../utils/styles';
import { GRID_METRIC_REGISTRY } from './gridMetricRegistry';
import { getCategoryColour } from '../utils/categoryColours';
import {
  SectionLabel,
  SectionDesc,
  CountRow,
  CountLabel,
  CountBadge,
} from './customizerStyles';

const MAX_METRICS = 12;

const DOMAINS = [
  { key: 'fuel', labelKey: 'domain_fuel' },
  { key: 'utilization', labelKey: 'domain_utilization' },
  { key: 'tco', labelKey: 'domain_tco' },
  { key: 'oc', labelKey: 'domain_oc' },
  { key: 'inspections', labelKey: 'domain_inspections' },
  { key: 'workorders', labelKey: 'domain_workorders' },
];

const DOMAIN_LABEL = {
  fuel:        'domain_fuel',
  utilization: 'domain_utilization',
  tco:         'domain_tco',
  oc:          'domain_oc',
  inspections: 'domain_inspections',
  workorders:  'domain_workorders',
};

// ── Selected list (drag to reorder) ───────────────────────────────────────────

const SelectedSection = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${colorFieldsBorder};
`;

const SlotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilledSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${colorHover};
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  padding: 10px 12px;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    border-color: ${colorGray};
  }
`;

const DragHandle = styled.span`
  color: ${colorTextAdditional};
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
`;

const SlotInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SlotDomain = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: ${colorTextAdditional};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 1px;
`;

const SlotName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colorText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: none;
  color: ${colorTextAdditional};
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
  font-family: inherit;

  &:hover {
    background: ${colorGray}22;
    color: ${colorGray};
  }
`;

// ── Domain accordion ───────────────────────────────────────────────────────────

const AddSection = styled.div`
  padding: 16px 24px 8px;
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 4px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: ${colorTextAdditional};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 32px;
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  font-size: 12px;
  font-family: inherit;
  color: ${colorText};
  background: ${colorBackground};
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${colorLightBlue};
  }

  &::placeholder {
    color: ${colorTextAdditional};
  }
`;

const DomainGroup = styled.div`
  border-bottom: 1px solid ${colorFieldsBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const DomainHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const DomainHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DomainDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  flex-shrink: 0;
`;

const DomainInfo = styled.div``;

const DomainName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colorText};
`;

const DomainCount = styled.div`
  font-size: 11px;
  color: ${colorTextAdditional};
`;

const DomainChevron = styled.span`
  font-size: 11px;
  color: ${colorTextAdditional};
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: inline-block;
`;

const DomainItems = styled.div`
  padding-bottom: 8px;
`;

const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  transition: opacity 0.15s;

  &:hover {
    ${({ $disabled }) => !$disabled && `opacity: 0.8;`}
  }
`;

const Checkbox = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${({ $active }) => ($active ? '#1B2A4A' : colorFieldsBorder)};
  border-radius: 4px;
  background: ${({ $active }) => ($active ? '#1B2A4A' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
`;

const CheckIcon = styled.span`
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;

const MetricName = styled.span`
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  //color: ${({ $active }) => ($active ? colorText : colorTextAdditional)};
  line-height: 1.3;
`;

const FlatResult = styled.div`
  padding: 8px 0;
  font-size: 12px;
  color: ${colorTextAdditional};
  font-style: italic;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function GridMetricsSection({ draftGridIds, setDraftGridIds }) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [openDomains, setOpenDomains] = useState(
    new Set(DOMAINS.map((d) => d.key))
  );

  const atMax = draftGridIds.length >= MAX_METRICS;

  function onDragEnd(result) {
    if (!result.destination) return;
    const next = Array.from(draftGridIds);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setDraftGridIds(next);
  }

  function removeMetric(id) {
    setDraftGridIds(draftGridIds.filter((m) => m !== id));
  }

  function toggleDomain(key) {
    setOpenDomains((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleMetric(id) {
    const active = draftGridIds.includes(id);
    if (!active && atMax) return;
    if (active) {
      setDraftGridIds(draftGridIds.filter((m) => m !== id));
    } else {
      setDraftGridIds([...draftGridIds, id]);
    }
  }

  const query = search.trim().toLowerCase();
  const filtered = query
    ? GRID_METRIC_REGISTRY.filter((m) => t(m.labelKey, { defaultValue: m.label }).toLowerCase().includes(query))
    : null;

  return (
    <>
      {/* ── Selected metrics (drag to reorder) ── */}
      <SelectedSection>
        <SectionLabel>{t('metrics_grid')}</SectionLabel>
        <SectionDesc>{t('metrics_grid_description')}</SectionDesc>
        <CountRow>
          <CountLabel>{t('selected')}</CountLabel>
          <CountBadge $atMax={atMax}>
            {draftGridIds.length} / {MAX_METRICS}
          </CountBadge>
        </CountRow>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="grid-metric-slots" direction="vertical">
            {(provided) => (
              <SlotList ref={provided.innerRef} {...provided.droppableProps}>
                {draftGridIds.map((id, index) => {
                  const config = GRID_METRIC_REGISTRY.find((m) => m.id === id);
                  if (!config) return null;
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(draggableProvided) => (
                        <FilledSlot
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <DragHandle>⠿</DragHandle>
                          <SlotInfo>
                            <SlotDomain>{t(DOMAIN_LABEL[config.domain] ?? config.domain)}</SlotDomain>
                            <SlotName>{t(config.labelKey, { defaultValue: config.label })}</SlotName>
                          </SlotInfo>
                          <RemoveButton onClick={() => removeMetric(id)}>✕</RemoveButton>
                        </FilledSlot>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </SlotList>
            )}
          </Droppable>
        </DragDropContext>
      </SelectedSection>

      {/* ── Add metrics (domain accordion + search) ── */}
      <AddSection>
        <SectionLabel style={{ marginBottom: 10 }}>{t('add_metrics')}</SectionLabel>
        <SearchWrapper>
          <SearchIcon>🔍</SearchIcon>
          <SearchInput
            placeholder={t('search_metrics')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrapper>
      </AddSection>

      {filtered ? (
        <div style={{ padding: '0 24px 12px' }}>
          {filtered.length === 0 ? (
            <FlatResult>{t('no_metrics_found')}</FlatResult>
          ) : (
            filtered.map((metric) => {
              const active = draftGridIds.includes(metric.id);
              const disabled = !active && atMax;
              return (
                <MetricItem
                  key={metric.id}
                  $disabled={disabled}
                  onClick={() => toggleMetric(metric.id)}
                >
                  <Checkbox $active={active}>
                    {active && <CheckIcon>✓</CheckIcon>}
                  </Checkbox>
                  <MetricName $active={active}>{t(metric.labelKey, { defaultValue: metric.label })}</MetricName>
                </MetricItem>
              );
            })
          )}
        </div>
      ) : (
        DOMAINS.map((domain) => {
          const metrics = GRID_METRIC_REGISTRY.filter((m) => m.domain === domain.key);
          const activeCount = metrics.filter((m) => draftGridIds.includes(m.id)).length;
          const isOpen = openDomains.has(domain.key);

          return (
            <DomainGroup key={domain.key}>
              <div style={{ padding: '0 24px' }}>
                <DomainHeader onClick={() => toggleDomain(domain.key)}>
                  <DomainHeaderLeft>
                    <DomainDot $colour={getCategoryColour(domain.key)} />
                    <DomainInfo>
                      <DomainName>{t(domain.labelKey)}</DomainName>
                      <DomainCount>
                        {t('metrics_count', { count: metrics.length })}{activeCount > 0 ? ` · ${t('metrics_selected_count', { count: activeCount })}` : ''}
                      </DomainCount>
                    </DomainInfo>
                  </DomainHeaderLeft>
                  <DomainChevron $open={isOpen}>▾</DomainChevron>
                </DomainHeader>

                {isOpen && (
                  <DomainItems>
                    {metrics.map((metric) => {
                      const active = draftGridIds.includes(metric.id);
                      const disabled = !active && atMax;
                      return (
                        <MetricItem
                          key={metric.id}
                          $disabled={disabled}
                          onClick={() => toggleMetric(metric.id)}
                        >
                          <Checkbox $active={active}>
                            {active && <CheckIcon>✓</CheckIcon>}
                          </Checkbox>
                          <MetricName $active={active}>{t(metric.labelKey, { defaultValue: metric.label })}</MetricName>
                        </MetricItem>
                      );
                    })}
                  </DomainItems>
                )}
              </div>
            </DomainGroup>
          );
        })
      )}
    </>
  );
}
